<script lang="ts">
	/**
	 * Email address with an explicit choice of how to send.
	 *
	 * A bare `mailto:` link is a dead end on locked-down machines and for
	 * webmail-only visitors: the click silently does nothing, which reads as a
	 * broken site. Browsers give no signal that the handler failed, so rather than
	 * detect failure we offer the alternatives up front.
	 */
	import { Check, Copy, ExternalLink, Mail, MailOpen } from 'lucide-svelte';

	type Props = {
		email: string;
		subject?: string;
		body?: string;
		/** Visible label; defaults to the address itself. */
		label?: string;
		/** Extra classes on the trigger, so callers can match their own type scale. */
		triggerClass?: string;
		class?: string;
	};

	let {
		email,
		subject = '',
		body = '',
		label,
		triggerClass = 'text-red-500 hover:text-red-600 font-medium',
		class: className = ''
	}: Props = $props();

	let open = $state(false);
	let copied = $state(false);
	let rootEl: HTMLDivElement | undefined;
	let triggerEl: HTMLButtonElement | undefined;
	let copyResetTimer: ReturnType<typeof setTimeout> | undefined;

	/**
	 * Not URLSearchParams: it encodes a space as `+`, which mail clients render
	 * literally in a subject line.
	 */
	function compose(prefix: string, subjectKey: string): string {
		const params = [
			subject && `${subjectKey}=${encodeURIComponent(subject)}`,
			body && `body=${encodeURIComponent(body)}`
		].filter(Boolean);
		if (params.length === 0) return prefix;
		return `${prefix}${prefix.includes('?') ? '&' : '?'}${params.join('&')}`;
	}

	const mailtoHref = $derived(compose(`mailto:${email}`, 'subject'));
	const gmailHref = $derived(
		compose(`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`, 'su')
	);
	const outlookHref = $derived(
		compose(
			`https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(email)}`,
			'subject'
		)
	);

	/** Legacy copy path, via a detached textarea. */
	function copyViaTextarea(text: string): boolean {
		const scratch = document.createElement('textarea');
		scratch.value = text;
		scratch.setAttribute('readonly', '');
		scratch.style.position = 'fixed';
		scratch.style.opacity = '0';
		document.body.appendChild(scratch);
		scratch.select();
		try {
			return document.execCommand('copy');
		} catch {
			return false;
		} finally {
			scratch.remove();
		}
	}

	/**
	 * `navigator.clipboard` is undefined outside secure contexts — the same
	 * restricted setups this component exists for — so fall back to execCommand.
	 */
	function writeToClipboard(text: string): Promise<boolean> {
		if (!navigator.clipboard?.writeText) return Promise.resolve(copyViaTextarea(text));
		return navigator.clipboard.writeText(text).then(
			() => true,
			() => copyViaTextarea(text)
		);
	}

	function copyAddress() {
		writeToClipboard(email).then((ok) => {
			if (!ok) return;
			copied = true;
			clearTimeout(copyResetTimer);
			copyResetTimer = setTimeout(() => {
				copied = false;
			}, 2000);
		});
	}

	$effect(() => {
		if (!open) return;

		const onClick = (event: MouseEvent) => {
			if (event.target instanceof Node && rootEl?.contains(event.target)) return;
			open = false;
		};
		const onKeydown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			open = false;
			triggerEl?.focus();
		};

		document.addEventListener('click', onClick);
		document.addEventListener('keydown', onKeydown);
		return () => {
			document.removeEventListener('click', onClick);
			document.removeEventListener('keydown', onKeydown);
		};
	});

	$effect(() => () => clearTimeout(copyResetTimer));
</script>

<div bind:this={rootEl} class="relative inline-flex items-center gap-1.5 {className}">
	<button
		bind:this={triggerEl}
		type="button"
		onclick={() => (open = !open)}
		aria-haspopup="menu"
		aria-expanded={open}
		class="inline-flex cursor-pointer items-center gap-2 transition-colors {triggerClass}"
	>
		<Mail class="h-4 w-4" />
		<!-- Entity-encoded to deter address harvesters, as this site has always
		     published its address. `email` is our own literal, never user input. -->
		<span>{@html (label ?? email).replace('@', '&#64;')}</span>
	</button>

	<!-- Copy without having to open the menu first. -->
	<button
		type="button"
		onclick={copyAddress}
		title="Copy address"
		aria-label={copied ? 'Address copied' : 'Copy address'}
		class="cursor-pointer rounded p-0.5 text-gray-400 transition-colors hover:text-red-500"
	>
		{#if copied}
			<Check class="h-3.5 w-3.5 text-green-600" />
		{:else}
			<Copy class="h-3.5 w-3.5" />
		{/if}
	</button>

	{#if open}
		<div
			role="menu"
			class="absolute left-0 z-50 mt-2 w-60 rounded-xl border border-slate-200 bg-white py-1 text-left shadow-lg"
		>
			<button
				type="button"
				role="menuitem"
				onclick={copyAddress}
				class="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
			>
				{#if copied}
					<Check class="h-4 w-4 flex-shrink-0 text-green-600" />
					<span class="text-green-700">Copied</span>
				{:else}
					<Copy class="h-4 w-4 flex-shrink-0 text-gray-400" />
					<span>Copy address</span>
				{/if}
			</button>
			<a
				role="menuitem"
				href={gmailHref}
				target="_blank"
				rel="noopener noreferrer"
				class="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
			>
				<ExternalLink class="h-4 w-4 flex-shrink-0 text-gray-400" />
				<span>Open in Gmail</span>
			</a>
			<a
				role="menuitem"
				href={outlookHref}
				target="_blank"
				rel="noopener noreferrer"
				class="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
			>
				<ExternalLink class="h-4 w-4 flex-shrink-0 text-gray-400" />
				<span>Open in Outlook</span>
			</a>
			<a
				role="menuitem"
				href={mailtoHref}
				class="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
			>
				<MailOpen class="h-4 w-4 flex-shrink-0 text-gray-400" />
				<span>Open default mail app</span>
			</a>
		</div>
	{/if}
</div>
